/**
 * Utility function to sort parts table data
 * 
 * Sorts by:
 * 1. Type orders field (ascending: 1, 10, 55...)
 * 2. Type name (alphabetically)
 * 3. Item Code/Part number (alphabetically)
 * 
 * @param {Array} data - Array of parts data
 * @param {string} dataFormat - "bom" or "step" format (default: "bom")
 * @returns {Array} Sorted array
 */
export const sortPartsTableData = (data, dataFormat = "bom") => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Helper function to extract type orders based on data format
  const getTypeOrders = (item) => {
    if (dataFormat === "step") {
      return parseInt(item.part_info?.type_info?.orders || 999999, 10);
    } else {
      // BOM format has orders at root level
      return parseInt(item.orders || 999999, 10);
    }
  };

  // Helper function to extract type name based on data format
  const getTypeName = (item) => {
    if (dataFormat === "step") {
      return (
        item.part_info?.type_info?.name ||
        item.part_info?.type_info?.type_name ||
        item.part_info?.type_name ||
        ""
      );
    } else {
      return item.type_name || "";
    }
  };

  // Helper function to extract part number based on data format
  const getPartNumber = (item) => {
    if (dataFormat === "step") {
      return item.part_info?.part_number || "";
    } else {
      return item.part_number || "";
    }
  };

  return data.slice().sort((a, b) => {
    // 1. Sort by Type orders field (ascending numerically)
    const orderA = getTypeOrders(a);
    const orderB = getTypeOrders(b);
    
    if (orderA !== orderB) return orderA - orderB;

    // 2. If type orders are equal, sort alphabetically by type name
    const typeNameA = getTypeName(a);
    const typeNameB = getTypeName(b);
    const typeNameComparison = typeNameA.localeCompare(typeNameB);
    
    if (typeNameComparison !== 0) return typeNameComparison;

    // 3. If type names are also equal, sort alphabetically by part_number
    const partNumberA = getPartNumber(a);
    const partNumberB = getPartNumber(b);

    // Sort alphabetically (character by character)
    return partNumberA.localeCompare(partNumberB);
  });
};

/**
 * Utility function to sort parts table data for new data format
 * 
 * Sorts by:
 * 1. Type orders field (ascending: 1, 10, 55...)
 * 2. Type field (Special characters, Numbers, Capital letters, Small letters)
 * 3. Part number (Special characters, Numbers, Capital letters, Small letters)
 * 
 * Works with data structure where:
 * - Orders are in part_info.type_details.orders
 * - Part number is at root level as partNumber
 * - Type is in part_info.type
 * 
 * @param {Array} data - Array of parts data
 * @returns {Array} Sorted array
 */
export const sortNewPartsTableData = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Helper function to extract type orders from new data format
  const getTypeOrders = (item) => {
    return parseInt(item.part_info?.type_details?.orders || 999999, 10);
  };

  // Helper function to extract type from new data format
  const getType = (item) => {
    return item.part_info?.type || "";
  };

  // Helper function to extract part number from new data format
  const getPartNumber = (item) => {
    return item.partNumber || "";
  };

  /**
   * Helper function to get character category priority
   * Returns: 0 = Special characters, 1 = Numbers, 2 = Capital letters, 3 = Small letters
   */
  const getCharCategory = (char) => {
    const code = char.charCodeAt(0);
    if (code >= 48 && code <= 57) return 1; // Numbers (0-9)
    if (code >= 65 && code <= 90) return 2; // Capital letters (A-Z)
    if (code >= 97 && code <= 122) return 3; // Small letters (a-z)
    return 0; // Special characters (everything else)
  };

  /**
   * Helper function to create a sort key for a string
   * Returns an array of [category, charCode] pairs for each character
   * This ensures proper sorting: Special chars < Numbers < Capitals < Small letters
   */
  const createSortKey = (str) => {
    if (!str) return [];
    return str.split('').map(char => {
      const category = getCharCategory(char);
      const charCode = char.charCodeAt(0);
      return [category, charCode];
    });
  };

  /**
   * Helper function to compare two sort keys
   * Returns: -1 if a < b, 0 if a === b, 1 if a > b
   */
  const compareSortKeys = (keyA, keyB) => {
    const minLength = Math.min(keyA.length, keyB.length);
    
    for (let i = 0; i < minLength; i++) {
      const [catA, codeA] = keyA[i];
      const [catB, codeB] = keyB[i];
      
      // First compare by category
      if (catA !== catB) {
        return catA - catB;
      }
      
      // If same category, compare by character code
      if (codeA !== codeB) {
        return codeA - codeB;
      }
    }
    
    // If all characters match up to minLength, shorter string comes first
    return keyA.length - keyB.length;
  };

  return data.slice().sort((a, b) => {
    // 1. Sort by Type orders field (ascending numerically)
    const orderA = getTypeOrders(a);
    const orderB = getTypeOrders(b);
    
    if (orderA !== orderB) return orderA - orderB;

    // 2. If type orders are equal, sort by type (Special chars, Numbers, Capitals, Small letters)
    const typeA = getType(a);
    const typeB = getType(b);
    
    const typeKeyA = createSortKey(typeA);
    const typeKeyB = createSortKey(typeB);
    const typeComparison = compareSortKeys(typeKeyA, typeKeyB);
    
    if (typeComparison !== 0) return typeComparison;

    // 3. If types are equal, sort by partNumber (Special chars, Numbers, Capitals, Small letters)
    const partNumberA = getPartNumber(a);
    const partNumberB = getPartNumber(b);
    
    const partKeyA = createSortKey(partNumberA);
    const partKeyB = createSortKey(partNumberB);
    
    return compareSortKeys(partKeyA, partKeyB);
  });
};